import {createSelector, createSlice} from "@reduxjs/toolkit";
import {applyTpl, capitalize} from "~/lib";
import {locationChange} from "~/reducers/app";
import type {PageInitialState} from "~/reducers/page.types";
import type {PageTplNameTypeFixed} from "~/components/templates/Page.props";
import {PageGroupTplNameTypeFixed} from "~/components/templates/PageGroup.props";
import {RouteParams} from "~/hooks.types";

const initialState: PageInitialState = {};

export const pageSlice = createSlice({
    name: "page",
    initialState,
    reducers: {init: (state) => state},
    extraReducers: builder => {
        builder.addCase(
            locationChange,
            (_, action) => ({
                title: action.payload.page && action.payload.page.title || undefined,
                template: action.payload.page && action.payload.page.template || undefined,
                groupTemplate: action.payload.page && action.payload.page.groupTemplate || undefined,
                slug: action.payload.slug,
                group: action.payload.page && action.payload.page.group || undefined,
            })
        );
    },
    selectors: {
        getPage: state => state,
        getPageSlug: state => state.slug,
        getPageGroup: state => state.group,
        getPageTemplates: createSelector(
            [
                state => state.template,
                state => state.slug,
            ],
            (template: PageTplNameTypeFixed, slug: string) => {
                const templates: (string | PageTplNameTypeFixed)[] = [];
                if (template) {
                    templates.push(template);
                }
                if (slug) {
                    templates.push(
                        slug,
                        slug.split("-").map(s => capitalize(s)).join("")
                    );
                }
                return templates;
            }
        ),
        getPageGroupTemplates: createSelector(
            [
                state => state.groupTemplate,
                state => state.group,
            ],
            (template: PageGroupTplNameTypeFixed, group: string) => {
                const templates: (string | PageGroupTplNameTypeFixed)[] = [];
                if (template) {
                    templates.push(template);
                }
                if (group) {
                    templates.push(
                        group,
                        group.split("-").map(s => capitalize(s)).join("")
                    );
                }
                return templates;
            }
        ),
        getPageTitle: createSelector(
            [state => state.title],
            (title: PageInitialState["title"]) => (routeParams?: RouteParams, searchParams?: URLSearchParams): string => {
                let titleTpl = (typeof title === "object" ? title.default : title) || "";
                const titleParams: {[key: string]: string} = {};
                if (searchParams) {
                    searchParams.forEach((v, k) => {
                        titleParams[k] = v;
                    });
                }
                if (routeParams) {
                    Object.entries(routeParams).forEach(([k, v]) => {
                        if (typeof v !== "undefined") {
                            titleParams[k] = typeof v === "boolean" ? (v ? "yes" : "no") : v;
                        }
                    });
                    if (routeParams.edit && typeof title === "object" && title.editable) {
                        titleTpl = title.editable;
                    }
                }

                return applyTpl(titleTpl, titleParams);
            }
        )
    },
});

export const {init} = pageSlice.actions;

export const {
    getPageTemplates,
    getPageGroupTemplates,
    getPageSlug,
    getPageGroup,
    getPageTitle,
} = pageSlice.selectors;

export default pageSlice;